(() => {
  if (window.peachyRealPeopleResultsInitialized) return;

  window.peachyRealPeopleResultsInitialized = true;
  const rootSelector = '[data-peachy-real-results]';

  const initializeSlider = (root) => {
    if (root.dataset.peachyRealResultsInitialized === 'true') return;

    const track = root.querySelector('[data-slider-track]');
    const slides = [...root.querySelectorAll('[data-slider-slide]')];
    const previousButton = root.querySelector('[data-slider-previous]');
    const nextButton = root.querySelector('[data-slider-next]');
    const pagination = root.querySelector('[data-slider-pagination]');

    if (!track || slides.length === 0 || typeof window.Swiper !== 'function') return;

    root.dataset.peachyRealResultsInitialized = 'true';

    const videoVisibilityObserver = typeof window.IntersectionObserver === 'function'
      ? new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) return;

            const video = entry.target.querySelector('video');
            if (video && !video.paused) video.pause();
          });
        })
      : null;

    root.peachyRealResultsVideoObserver = videoVisibilityObserver;

    const formatTime = (seconds) => {
      const total = Math.max(0, Math.floor(Number(seconds) || 0));
      const minutes = Math.floor(total / 60);
      const remainder = total % 60;
      return minutes + ':' + String(remainder).padStart(2, '0');
    };

    root.querySelectorAll('[data-video-card]').forEach((card) => {
      const video = card.querySelector('video');
      const playbackButton = card.querySelector('[data-video-toggle]');
      const muteButton = card.querySelector('[data-video-mute-toggle]');
      const rewindButton = card.querySelector('[data-video-rewind]');
      const forwardButton = card.querySelector('[data-video-forward]');
      const timeline = card.querySelector('[data-video-timeline]');
      const currentTime = card.querySelector('[data-video-current]');
      const remainingTime = card.querySelector('[data-video-remaining]');
      if (!video || !playbackButton || !muteButton || !timeline) return;

      videoVisibilityObserver?.observe(card);

      const updatePlaybackState = () => {
        const isPlaying = !video.paused && !video.ended;
        card.classList.toggle('is-playing', isPlaying);
        playbackButton.setAttribute('aria-label', isPlaying ? root.dataset.pauseLabel : root.dataset.playLabel);
        playbackButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      };

      const updateMuteState = () => {
        card.classList.toggle('is-muted', video.muted);
        muteButton.setAttribute('aria-label', video.muted ? root.dataset.unmuteLabel : root.dataset.muteLabel);
        muteButton.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
      };

      const updateTimeline = () => {
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const time = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const progress = duration > 0 ? (time / duration) * 100 : 0;
        timeline.max = String(duration || 0);
        if (document.activeElement !== timeline) timeline.value = String(time);
        timeline.style.setProperty('--prr-video-progress', progress + '%');
        if (currentTime) currentTime.textContent = formatTime(time);
        if (remainingTime) remainingTime.textContent = '-' + formatTime(Math.max(0, duration - time));
      };

      const seekBy = (delta) => {
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const next = Math.min(Math.max(0, video.currentTime + delta), duration || video.currentTime + delta);
        video.currentTime = next;
        updateTimeline();
      };

      const muteOtherVideos = () => {
        document.querySelectorAll('.peachy-real-people-results__video').forEach((otherVideo) => {
          if (otherVideo !== video) otherVideo.muted = true;
        });
      };

      let hideControlsTimer;
      const revealControls = () => {
        card.classList.add('is-controls-visible');
        clearTimeout(hideControlsTimer);
        hideControlsTimer = setTimeout(() => {
          card.classList.remove('is-controls-visible');
        }, 2500);
      };

      card.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' || !card.classList.contains('is-playing')) return;
        revealControls();
      });

      playbackButton.addEventListener('click', () => {
        if (video.paused || video.ended) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });

      rewindButton?.addEventListener('click', () => seekBy(-10));
      forwardButton?.addEventListener('click', () => seekBy(10));

      timeline.addEventListener('input', () => {
        video.currentTime = Number(timeline.value) || 0;
        updateTimeline();
      });

      muteButton.addEventListener('click', () => {
        if (video.muted) {
          muteOtherVideos();
          video.muted = false;
        } else {
          video.muted = true;
        }
        updateMuteState();
      });

      video.addEventListener('play', updatePlaybackState);
      video.addEventListener('pause', updatePlaybackState);
      video.addEventListener('ended', updatePlaybackState);
      video.addEventListener('timeupdate', updateTimeline);
      video.addEventListener('loadedmetadata', updateTimeline);
      video.addEventListener('durationchange', updateTimeline);
      video.addEventListener('volumechange', () => {
        if (!video.muted) muteOtherVideos();
        updateMuteState();
      });

      video.autoplay = false;
      video.muted = false;
      video.defaultMuted = false;
      video.pause();
      updatePlaybackState();
      updateMuteState();
      updateTimeline();
    });

    root.peachyRealResultsSwiper = new window.Swiper(track, {
      slidesPerView: 'auto',
      spaceBetween: 24,
      freeMode: {
        enabled: true
      },
      keyboard: {
        enabled: true
      },
      navigation: {
        enabled: false,
        prevEl: previousButton,
        nextEl: nextButton
      },
      pagination: pagination
        ? {
            el: pagination,
            clickable: true
          }
        : undefined,
      breakpoints: {
        1200: {
          freeMode: {
            enabled: false
          },
          navigation: {
            enabled: true
          }
        }
      }
    });
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(rootSelector)) initializeSlider(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initializeSlider);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(rootSelector)
      ? event.target
      : event.target.querySelector?.(rootSelector);
    root?.peachyRealResultsVideoObserver?.disconnect();
    root?.peachyRealResultsSwiper?.destroy(true, true);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-slider-slide]');
    const root = slide?.closest(rootSelector);
    if (!slide || !root) return;

    root.peachyRealResultsSwiper?.slideTo(Number(slide.dataset.sliderSlide));
  });
})();
